import React, { useMemo } from "react";
import { Tabs } from "antd";
import { useSearchParams } from "react-router-dom";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";

import { usePermission } from "~/hooks/usePermission";
import ChatResponseMetrics from "./pages/ChatResponseMetrics/ChatResponseMetrics";
import TeamMemberList from "./pages/TeamMemberList/TeamMemberList";
import TeamMembersStats from "./pages/TeamMembersStats/TeamMembersStats";
import TeamRolesTab from "./pages/TeamRoles/TeamRolesTab";

export type TeamAreaTabKey = "members" | "stats" | "chat-metrics";

// Jedna strona /team z tabami ograniczanymi per uprawnienie zastępuje trzy
// osobne wejścia (lista + /team/stats + /team/chat-metrics): head coach widzi
// tu tylko Statystyki, administracja i właściciel komplet.
const TeamArea = (): React.ReactElement => {
  const canReadMembers = usePermission(PermissionKey.TEAM_READ);
  const canReadTeamStats = usePermission(PermissionKey.TEAM_STATS_READ, PermissionScope.ALL);
  const canReadChatMetrics = usePermission(PermissionKey.TEAM_CHAT_METRICS_READ, PermissionScope.ALL);
  const canManagePermissions = usePermission(PermissionKey.TEAM_PERMISSIONS_MANAGE, PermissionScope.ALL);
  const [searchParams, setSearchParams] = useSearchParams();

  const items = useMemo(
    () =>
      [
        canReadMembers ? { key: "members", label: "Członkowie", children: <TeamMemberList /> } : null,
        canReadTeamStats ? { key: "stats", label: "Rozliczenia", children: <TeamMembersStats /> } : null,
        canReadChatMetrics ? { key: "chat-metrics", label: "Statystyki", children: <ChatResponseMetrics /> } : null,
        // Sam klucz team.permissions.manage @ ALL — dopisany `isOwner` czynił go
        // martwym dla kogokolwiek poza właścicielem, mimo że jest nadawalny.
        canManagePermissions ? { key: "roles", label: "Role", children: <TeamRolesTab /> } : null,
      ].filter((item): item is NonNullable<typeof item> => item !== null),
    [canReadMembers, canReadTeamStats, canReadChatMetrics, canManagePermissions],
  );

  const requestedTab = searchParams.get("tab");
  const activeTab = items.find((item) => item.key === requestedTab)?.key ?? items[0]?.key;

  return <Tabs activeKey={activeTab} onChange={(key) => setSearchParams({ tab: key })} items={items} destroyOnHidden />;
};

export default TeamArea;
